import {
  createSlice,
  ActionReducerMapBuilder,
  createAction,
} from '@reduxjs/toolkit'
import {
  EndpointsItemsTypes,
  EndpointsLastEvaluatedKeyTypes,
  SliceStatus,
  EndpointAssociatedUser,
  SearchEndpointsLastEvaluatedKeyTypes,
  EndpointMetaDataType,
  DiagnosticResultType,
} from 'interfaces'
import {
  ToggleEndpointResult,
  toggleEndpoint,
  toggleEndpointSuccess,
  toggleEndpointFailure,
  getAssociatedUsers,
  getAssociatedUsersSuccess,
  getAssociatedUsersFailure,
  getDiagnosticResult,
  getDiagnosticResultSuccess,
  getDiagnosticResultFailure,
} from './endpointsActions'

const resetAssociatedUsers = createAction('resetAssociatedUsers')
const resetEndpointDiagnostic = createAction('resetEndpointDiagnostic')
const startDiagnosticPolling = createAction('startDiagnosticPolling')
const stopDiagnosticPolling = createAction('stopDiagnosticPolling')
const setSelectedLocation = createAction<string>('setSelectedLocation')
const setSelectedEndpoint = createAction<EndpointsItemsTypes>(
  'setSelectedEndpoint',
)
const updateEndpointList =
  createAction<EndpointsItemsTypes[]>('updateEndpointList')

type EndpointsSliceState = {
  items: EndpointsItemsTypes[]
  associatedUser: EndpointAssociatedUser[]
  lastEvaluateKey:
    | EndpointsLastEvaluatedKeyTypes
    | SearchEndpointsLastEvaluatedKeyTypes
  hasMore: boolean
  endpointMetaData: EndpointMetaDataType
  status: string
  activeToggleRequestId: string | null
  toggleResult: (ToggleEndpointResult & {status: SliceStatus}) | null
  error: string
  selectedEndpoints: EndpointsItemsTypes
  diagnostic: {
    result: DiagnosticResultType
    url: string
  }
  diagnosticStatus: SliceStatus
  pollingDiagnosticReport: boolean
  metaDataStatus: string
}
const endpointsSlice = createSlice({
  name: 'endpoints',
  initialState: {
    items: [] as EndpointsItemsTypes[],
    associatedUser: [] as EndpointAssociatedUser[],
    endpointMetaData: {} as EndpointMetaDataType,
    lastEvaluateKey: {} as
      | EndpointsLastEvaluatedKeyTypes
      | SearchEndpointsLastEvaluatedKeyTypes,
    status: SliceStatus.idle,
    activeToggleRequestId: null as string | null,
    toggleResult: null as (ToggleEndpointResult & {status: SliceStatus}) | null,
    hasMore: true,
    error: '',
    selectedEndpoints: {} as EndpointsItemsTypes,
    diagnostic: {
      result: {} as DiagnosticResultType,
      url: '',
      file_timestamp: '',
    },
    pollingDiagnosticReport: false,
    metaDataStatus: SliceStatus.idle,
    diagnosticStatus: SliceStatus.idle,
    selectedLocation: '',
  },
  reducers: {},
  extraReducers: (builder: ActionReducerMapBuilder<EndpointsSliceState>) =>
    builder
      .addCase(toggleEndpoint, (state, action) => ({
        ...state,
        status: SliceStatus.pending,
        activeToggleRequestId: action.meta.requestId,
        toggleResult: null,
      }))
      .addCase(toggleEndpointSuccess, (state, action) => ({
        ...state,
        status: SliceStatus.resolved,
        toggleResult: {...action.payload, status: SliceStatus.resolved},
      }))
      .addCase(toggleEndpointFailure, (state, action) => ({
        ...state,
        status: SliceStatus.rejected,
        toggleResult: {...action.payload, status: SliceStatus.rejected},
      }))
      .addCase(getDiagnosticResult, state => ({
        ...state,
        diagnosticStatus: SliceStatus.pending,
      }))
      .addCase(getDiagnosticResultSuccess, (state, action) => ({
        ...state,
        diagnostic: {
          result: action.payload.results,
          url: action.payload.download_url,
          file_timestamp: action.payload.file_timestamp,
        },
        diagnosticStatus: SliceStatus.resolved,
      }))
      .addCase(getDiagnosticResultFailure, state => ({
        ...state,
        diagnosticStatus: SliceStatus.rejected,
      }))
      .addCase(getAssociatedUsers, state => ({
        ...state,
        associatedUser: [] as EndpointAssociatedUser[],
        status: SliceStatus.pending,
      }))
      .addCase(getAssociatedUsersSuccess, (state, action) => ({
        ...state,
        associatedUser: action.payload.users,
        status: SliceStatus.rejected,
      }))
      .addCase(resetAssociatedUsers, state => ({
        ...state,
        associatedUser: [] as EndpointAssociatedUser[],
      }))
      .addCase(setSelectedEndpoint, (state, action) => ({
        ...state,
        selectedEndpoints: action.payload,
      }))
      .addCase(getAssociatedUsersFailure, state => ({
        ...state,
        status: SliceStatus.resolved,
      }))
      .addCase(startDiagnosticPolling, state => ({
        ...state,
        pollingDiagnosticReport: true,
      }))
      .addCase(stopDiagnosticPolling, state => ({
        ...state,
        pollingDiagnosticReport: false,
      }))
      .addCase(updateEndpointList, (state, action) => ({
        ...state,
        items: action.payload,
      }))
      .addCase(resetEndpointDiagnostic, state => ({
        ...state,
        diagnostic: {
          result: {} as DiagnosticResultType,
          url: '',
        },
      }))
      .addCase(setSelectedLocation, (state, action) => ({
        ...state,
        selectedLocation: action.payload,
      }))
      .addDefaultCase(state => state),
})

export const {reducer: endpointsReducer, name: endpointsReducerName} =
  endpointsSlice

export type TEndpointsActions =
  | ReturnType<typeof setSelectedEndpoint>
  | ReturnType<typeof toggleEndpoint>
  | ReturnType<typeof toggleEndpointSuccess>
  | ReturnType<typeof toggleEndpointFailure>
  | ReturnType<typeof getAssociatedUsers>
  | ReturnType<typeof getAssociatedUsersSuccess>
  | ReturnType<typeof getAssociatedUsersFailure>
  | ReturnType<typeof resetAssociatedUsers>
  | ReturnType<typeof getDiagnosticResult>
  | ReturnType<typeof getDiagnosticResultSuccess>
  | ReturnType<typeof getDiagnosticResultFailure>
  | ReturnType<typeof startDiagnosticPolling>
  | ReturnType<typeof stopDiagnosticPolling>
  | ReturnType<typeof updateEndpointList>
  | ReturnType<typeof resetEndpointDiagnostic>
  | ReturnType<typeof setSelectedLocation>

export const endpointsActions = {
  toggleEndpoint,
  toggleEndpointSuccess,
  toggleEndpointFailure,
  getAssociatedUsers,
  getAssociatedUsersSuccess,
  getAssociatedUsersFailure,
  setSelectedEndpoint,
  getDiagnosticResult,
  getDiagnosticResultSuccess,
  getDiagnosticResultFailure,
  startDiagnosticPolling,
  stopDiagnosticPolling,
  updateEndpointList,
  resetEndpointDiagnostic,
  setSelectedLocation,
  resetAssociatedUsers,
}

export type EndpointsState = ReturnType<typeof endpointsReducer>
